import React, {ReactNode, useState} from 'react';
import {ChevronDown} from '@carbon/icons-react';
import getFormatter from '../../../../lib/formatter';
import type {ComparisonMeasure} from '../../lib/stores/roster-comparison-store';
import RosterName from './roster-name';
import {GapWarning, placeName, SummaryNotice} from './summary-notices';
import type {ComparisonSummary, RankedRoster} from './summarise';

// The table's own precision, so a figure here reads the same as the cells beside it
const percent = getFormatter('percentfloor', .1);

// Splits listed before "Show all" - enough to scan, short enough to keep the panel glanceable
const SPLITS_SHOWN = 8;

const plural = (count: number, one: string, many: string) => (count === 1 ? one : many);

// A comparison holds at most five rosters, so ranks never reach the 11th-13th exceptions
const ORDINALS = ['1st', '2nd', '3rd', '4th', '5th'];
const ordinal = (n: number) => ORDINALS[n - 1] ?? `${n}th`;

// How the scope line names the measure; Combined reads both standards, as the table's Combined view does
const MEASURE_LABELS: Record<ComparisonMeasure, string> = {
    combined: 'time and distance',
    time: 'time',
    distance: 'distance',
};

type FoldId = 'primary' | 'splits';

export type SummaryDisclosures = {
    folds: Record<FoldId, boolean>;
    toggleFold: (id: FoldId) => void;
    allSplits: boolean;
    toggleAllSplits: () => void;
    // Whether the data-gap warning lists the specialties it leaves out
    allLeftOut: boolean;
    toggleAllLeftOut: () => void;
};

/**
 * Open/closed state of the panel's folds and its split list. Owned by the panel rather than the
 * content, which unmounts while a new comparison loads, so a fold stays as the user left it
 */
export function useSummaryDisclosures(): SummaryDisclosures {
    const [folds, setFolds] = useState<Record<FoldId, boolean>>({primary: true, splits: true});
    const [allSplits, setAllSplits] = useState(false);
    const [allLeftOut, setAllLeftOut] = useState(false);
    return {
        folds,
        toggleFold: (id) => setFolds((current) => ({...current, [id]: !current[id]})),
        allSplits,
        toggleAllSplits: () => setAllSplits((all) => !all),
        allLeftOut,
        toggleAllLeftOut: () => setAllLeftOut((all) => !all),
    };
}

// "a", "a and b", "a, b and c"
const joinWithAnd = (items: ReactNode[]) => items.map((item, i) => (
    <React.Fragment key={i}>
        {i > 0 && <span className='summary-panel__cap'>{i === items.length - 1 ? ' and ' : ', '}</span>}
        {item}
    </React.Fragment>
));

type FoldProps = {
    id: string;
    title: string;
    open: boolean;
    onToggle: () => void;
    children: ReactNode;
};

// A disclosure: the heading wraps the button, as the button cannot contain a heading
const Fold = ({id, title, open, onToggle, children}: FoldProps) => (
    <section className='summary-panel__fold'>
        <h3 className='summary-panel__section-title'>
            <button
                type='button'
                className='summary-panel__fold-btn'
                aria-expanded={open}
                aria-controls={`${id}-body`}
                onClick={onToggle}
            >
                <ChevronDown size={16} className='summary-panel__fold-icon' aria-hidden='true' />
                {title}
            </button>
        </h3>
        <div id={`${id}-body`} className='summary-panel__fold-body' hidden={!open}>
            {children}
        </div>
    </section>
);

type RankingProps = {
    ranked: RankedRoster[];
};

type LeadProps = RankingProps & {
    // Every roster has the same figures, so none trails the others
    identical: boolean;
};

// A roster with no figures shows a dash rather than a percentage it doesn't have
const formatAverage = (average: number | null) => (average === null ? '—' : percent.format(average));

// The one verdict to leave with. Leadership is claimed only when the DISPLAYED figures differ,
// so the words never contradict two percentages that read the same
const Lead = ({ranked, identical}: LeadProps) => {
    const best = ranked[0];
    const primaryRank = ranked.findIndex((r) => r.isPrimary);
    const primary = ranked[primaryRank];

    // Rosters with no figures rank last, so a best without one means none has any
    if (best.average === null) {
        return (
            <div className='summary-panel__lead' data-testid='summary-lead'>
                <p className='summary-panel__helper'>No roster has compliance figures for this selection.</p>
            </div>
        );
    }
    // Naming some rosters as sharing the highest would imply the rest trail, so every one is named
    if (identical) {
        return (
            <div className='summary-panel__lead' data-testid='summary-lead'>
                <p className='summary-panel__lead-who'>
                    {joinWithAnd(ranked.map((r) => <RosterName key={r.key} roster={r} />))}{' '}
                    <span className='summary-panel__cap'>share the same average</span>
                </p>
                <p className='summary-panel__lead-value'>{percent.format(best.average)}</p>
            </div>
        );
    }
    const leaders = ranked.filter((r) => r.average !== null && percent.format(r.average) === percent.format(best.average as number));

    if (leaders.length > 1) {
        return (
            <div className='summary-panel__lead' data-testid='summary-lead'>
                <p className='summary-panel__lead-who'>
                    {joinWithAnd(leaders.map((r) => <RosterName key={r.key} roster={r} />))}{' '}
                    <span className='summary-panel__cap'>share the highest average</span>
                </p>
                <p className='summary-panel__lead-value'>{percent.format(best.average)}</p>
                <p className='summary-panel__helper'>No single roster leads on average compliance.</p>
            </div>
        );
    }

    return (
        <div className='summary-panel__lead' data-testid='summary-lead'>
            <p className='summary-panel__lead-who'>
                <RosterName roster={best} />{' '}
                <span className='summary-panel__cap'>has the highest average compliance</span>
            </p>
            <p className='summary-panel__lead-value'>{percent.format(best.average)}</p>
            {primary && (
                <p className='summary-panel__helper'>
                    {best.isPrimary
                        ? 'That is your primary roster.'
                        : primary.average === null
                            ? `Your primary, ${primary.name}, has no compliance figures for this selection.`
                            : `Your primary, ${primary.name}, ranks ${ordinal(primaryRank + 1)} at ${percent.format(primary.average)}.`}
                </p>
            )}
        </div>
    );
};

// One series across the rosters, so colouring each bar by its roster is safe: every bar is
// direct-labelled with badge, name and value, and identity never rests on colour alone
const Ranking = ({ranked}: RankingProps) => (
    <section>
        <h3 className='summary-panel__section-title'>Average compliance</h3>
        <ul className='summary-panel__ranking' data-testid='summary-ranking'>
            {ranked.map((roster) => (
                <li key={roster.key} className='summary-panel__rank-row'>
                    <RosterName roster={roster} />{' '}
                    <span className={`summary-panel__rank-value${roster.average === null ? ' summary-panel__rank-value--empty' : ''}`}>
                        {formatAverage(roster.average)}
                    </span>
                    {/* The row's text already states the value, so the bar adds nothing to read out */}
                    <span className='summary-panel__bar' aria-hidden='true'>
                        <span
                            className={`summary-panel__bar-fill summary-panel__bar-fill--${roster.slot}`}
                            style={{width: `${Math.min(100, Math.max(0, (roster.average ?? 0) * 100))}%`}}
                        />
                    </span>
                </li>
            ))}
        </ul>
    </section>
);

type Props = {
    summary: ComparisonSummary;
    measure: ComparisonMeasure;
    // Specialty the Specialty filter narrows the comparison to; empty when it is not filtered
    specialty: string;
    // County, ZIP or state the comparison is scoped to; omitted from the scope line when empty
    geography: string;
    // What the geography grain calls one place: 'county' or 'ZIP code'
    noun: string;
    disclosures: SummaryDisclosures;
};

// The two supporting tallies
const Tallies = ({splits, largestGap}: Pick<ComparisonSummary, 'splits' | 'largestGap'>) => (
    <div className='summary-panel__tiles'>
        <div className='summary-panel__tile' data-testid='summary-differences'>
            <p className='summary-panel__tile-value'>{splits.length}</p>
            <p className='summary-panel__helper'>
                {plural(splits.length, 'specialty', 'specialties')} where the rosters disagree
            </p>
        </div>
        <div className='summary-panel__tile' data-testid='summary-largest-gap'>
            <p className='summary-panel__tile-value'>{largestGap ? percent.format(largestGap.spread) : '—'}</p>
            <p className='summary-panel__helper'>
                largest gap{largestGap ? `, in ${largestGap.name}` : ''}
            </p>
        </div>
    </div>
);

type DetailProps = {
    summary: ComparisonSummary;
    disclosures: SummaryDisclosures;
};

// The detail that folds away: each roster against the primary, then the specialties that split them
const Detail = ({summary, disclosures}: DetailProps) => {
    const {folds, toggleFold, allSplits, toggleAllSplits} = disclosures;
    const {ranked, splits, againstPrimary} = summary;
    const shownSplits = allSplits ? splits : splits.slice(0, SPLITS_SHOWN);

    return (
        <>
            <Fold
                id='summary-against-primary'
                title='Against primary'
                open={folds.primary}
                onToggle={() => toggleFold('primary')}
            >
                <ul className='summary-panel__insights'>
                    {againstPrimary.map((roster) => (
                        <li key={roster.key}>
                            {/* Flex items render no whitespace, but assistive tech needs it between them */}
                            <RosterName roster={roster} />{' '}
                            <span>
                                meets <strong>{roster.better}</strong> {plural(roster.better, 'specialty', 'specialties')}{' '}
                                the primary misses, and misses <strong>{roster.worse}</strong> it meets.
                            </span>
                        </li>
                    ))}
                </ul>
            </Fold>

            <Fold
                id='summary-splits'
                title='Specialties that split the rosters'
                open={folds.splits}
                onToggle={() => toggleFold('splits')}
            >
                {splits.length === 0 ? (
                    <p className='summary-panel__helper'>Every roster agrees on every specialty in scope.</p>
                ) : (
                    <>
                        <ul id='summary-splits-list' className='summary-panel__insights'>
                            {shownSplits.map((split, i) => (
                                <li key={`${split.name}-${i}`}>
                                    <span>
                                        <strong>{split.name}</strong> — met by {split.met} of {ranked.length}
                                    </span>
                                </li>
                            ))}
                        </ul>
                        {splits.length > SPLITS_SHOWN && (
                            <button
                                type='button'
                                className='summary-panel__more'
                                aria-expanded={allSplits}
                                aria-controls='summary-splits-list'
                                onClick={toggleAllSplits}
                            >
                                {allSplits ? 'Show fewer' : `Show all ${splits.length}`}
                            </button>
                        )}
                    </>
                )}
            </Fold>
        </>
    );
};

/**
 * The summary of a loaded comparison, ordered by priority: any data gap it leaves out, what is
 * compared, the verdict, the two supporting tallies, the ranking, then the detail that folds away
 */
const SummaryContent = ({summary, measure, specialty, geography, noun, disclosures}: Props) => {
    const {ranked, splits, largestGap, specialtyCount, scopeCount, excluded, leftOut, identical} = summary;

    return (
        <>
            {/* First, so every figure below is read knowing what it leaves out */}
            {(excluded.length > 0 || leftOut.length > 0) && (
                <GapWarning
                    summary={summary}
                    place={placeName(geography, noun)}
                    showLeftOut={disclosures.allLeftOut}
                    onToggleLeftOut={disclosures.toggleAllLeftOut}
                />
            )}

            {/* "36 of 42" when a data gap narrows it, so the scope reads right with the warning out of view */}
            <p className='summary-panel__scope' data-testid='summary-scope'>
                <strong>{ranked.length} rosters</strong> compared{' '}
                {specialty ? (
                    <>for <strong>{specialty}</strong></>
                ) : (
                    <>
                        across <strong>{specialtyCount}{specialtyCount < scopeCount && ` of ${scopeCount}`}</strong>{' '}
                        {plural(scopeCount, 'specialty', 'specialties')}
                    </>
                )}
                {geography && <> in <strong>{geography}</strong></>}, on {MEASURE_LABELS[measure]}.
            </p>

            <Lead ranked={ranked} identical={identical} />

            {/* With identical rosters the reason there are no differences or gaps stands in for the
                tallies. Info, not success: identical is a fact, not good news */}
            {identical ? (
                <SummaryNotice
                    kind='info'
                    title='Identical results'
                    body='These rosters have the same figures in every specialty in scope, so there are no differences or gaps to show.'
                    data-testid='summary-identical'
                />
            ) : (
                <Tallies splits={splits} largestGap={largestGap} />
            )}

            <Ranking ranked={ranked} />

            {/* Left out when identical: the folds could only say "0" and "none", which the notice says once */}
            {!identical && <Detail summary={summary} disclosures={disclosures} />}
        </>
    );
};

export default SummaryContent;
